import { inspectionDraftCleared, inspectionDraftStorageFailed } from "./inspectionTelemetry";

/**
 * localStorage-backed draft store for in-progress inspections.
 *
 * A technician works through a vehicle for several minutes, recording what they
 * find at each wheel as they get to it. Until now none of that existed anywhere
 * but React state, so anything that unmounted the inspection dialog threw the
 * whole report away. Drafts are written as the tech types and read back when the
 * inspection is reopened, so the loss is recoverable no matter what closed it.
 *
 * Deliberately local-only: nothing is sent to the backend until the tech
 * submits. Drafts are keyed by job so a tech with two jobs open can't have one
 * inspection bleed into the other.
 */
const STORAGE_PREFIX = "nubrakes:inspection-draft:v1:";

/**
 * Drafts outlive the job they belong to only long enough to be useful. A week
 * covers "the app crashed and the tech came back the next morning" without
 * letting stale values reappear on a job revisited months later.
 */
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type StoredDraft<T> = {
  jobId: string;
  savedAt: number;
  values: T;
};

const keyFor = (jobId: string) => `${STORAGE_PREFIX}${jobId}`;

/**
 * Safari private mode and a full storage quota both throw on access, and a tech
 * in a driveway is exactly who would hit that. Storage is best-effort: a failure
 * degrades to today's behaviour (no draft) rather than breaking the inspection.
 */
const safely = <T>(jobId: string, operation: string, fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch (error) {
    inspectionDraftStorageFailed(jobId, operation, error);
    return fallback;
  }
};

export const readDraft = <T>(jobId: string): StoredDraft<T> | null =>
  safely(
    jobId,
    "read",
    () => {
      const raw = window.localStorage.getItem(keyFor(jobId));
      if (!raw) return null;

      const parsed = JSON.parse(raw) as StoredDraft<T>;
      if (!parsed || typeof parsed !== "object" || !parsed.values) return null;

      if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
        window.localStorage.removeItem(keyFor(jobId));
        return null;
      }
      return parsed;
    },
    null
  );

export const writeDraft = <T>(jobId: string, values: T): void =>
  safely(
    jobId,
    "write",
    () => {
      const draft: StoredDraft<T> = { jobId, savedAt: Date.now(), values };
      window.localStorage.setItem(keyFor(jobId), JSON.stringify(draft));
    },
    undefined
  );

export const clearDraft = (jobId: string, reason: string): void =>
  safely(
    jobId,
    "clear",
    () => {
      window.localStorage.removeItem(keyFor(jobId));
      inspectionDraftCleared(jobId, reason);
    },
    undefined
  );

/**
 * Drop drafts past their TTL. Called once on app start so a tech's device
 * doesn't accumulate abandoned inspections indefinitely.
 */
export const pruneExpiredDrafts = (): void =>
  safely(
    "*",
    "prune",
    () => {
      const now = Date.now();
      const expired: string[] = [];

      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (!key || !key.startsWith(STORAGE_PREFIX)) continue;

        try {
          const parsed = JSON.parse(window.localStorage.getItem(key)) as StoredDraft<unknown>;
          if (!parsed?.savedAt || now - parsed.savedAt > DRAFT_TTL_MS) expired.push(key);
        } catch {
          // Unparseable entry under our prefix is junk; drop it.
          expired.push(key);
        }
      }

      expired.forEach((key) => window.localStorage.removeItem(key));
    },
    undefined
  );
